<template>
  <div class="seats-container">
    <div class="seats-wrapper" ref="seatWrapper">
      <div
        ref="seatContent"
        :style="{
          transform: `scale(${fullsizeRatio || zoom})`
        }"
        @mousedown="e => dragStart(e)"
        @mousemove="e => dragMove(e)"
        @mouseup="e => dragEnd(e)"
        class="seats-content"
      >
        <div
          class="seat-area"
        >
          <div class="seat-row">
            <div class="stage text-center mb-3">
              {{ $t('seat_selector_stage_direction') }}
              <div class="before"><img src="@/assets/image/stage-arrow@2x.png" srcset="@/assets/image/stage-arrow@3x.png" height="28" /></div>
              <div class="after"><img src="@/assets/image/stage-arrow@2x.png" srcset="@/assets/image/stage-arrow@3x.png" height="28" /></div>
            </div>
          </div>
          <div class="seats">
            <div
              class="seat-row"
              v-for="(row, i) in seats"
              :key="`seats-row-${i}`"
            >
              <div
                :class="{
                  seat: true,
                  'is-empty': !seat,
                  'is-avaliable': !!seat && seat.type === 'AVAILABLE' && seat.status === 'AVAILABLE',
                  'is-selected': !!seat && seat.type === 'AVAILABLE' && seat.status === 'RESERVED' && !isSelected(seat, 'origin'),
                  'is-unavaliable':
                    !!seat && !!seat.area && 
                    (seat.type === 'UNAVAILABLE' || seat.status === 'CONFIRMED'),
                  'is-hidden': !!seat && seat.type === 'HIDDEN',
                  'is-not-seat': !!seat && seat.type === 'NOT_SEAT',
                  'is-row-name': !!seat && seat.isRowName,
                  'is-active': !!seat && isSelected(seat),
                }"
                :data-row="!!seat ? seat.row : ''"
                :data-column="!!seat ? seat.column : ''"
                v-for="(seat, j) in row"
                :key="`seats-seat-${j}`"
                @click="e => toggleSeat(e, seat)"
              >
                <template v-if="!!seat">
                  <template v-if="seat.isRowName">
                    <span class="text-eight">
                      <span class="text-nine">{{ seat.rowName }}</span
                      >{{ $t('seat_row') }}
                    </span>
                  </template>
                  <template v-e;se>
                    <v-icon color="white" v-if="isSelected(seat)"
                      >mdi-check</v-icon
                    >
                    <template v-else>{{ seat.column }}</template>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 位置狀態示意 START -->
    <div class="seat-status">
      <div class="seat-item">
        <span class="seat is-avaliable"></span> <span class="text">{{ $t('seat_selector_avaliable') }}</span>
      </div>
      <div class="seat-item">
        <span class="seat is-active"></span> <span class="text">{{ $t('seat_selector_active') }}</span>
      </div>
      <div class="seat-item">
        <span class="seat is-selected"></span> <span class="text">{{ $t('seat_selector_selected') }}</span>
      </div>
      <div class="seat-item">
        <span class="seat is-unavaliable"></span>
        <span class="text">{{ $t('seat_selector_sold') }}</span>
      </div>
    </div>
    <!-- 位置狀態示意 END -->
    <!-- CONTROL BUTTON START -->
    <div class="control-bar">
      <div class="control-buttons">
        <v-btn class="control-button" :disabled="zoom >= 1.3" @click="zoomIn()">
          <v-icon color="primary">mdi-magnify-plus-outline</v-icon>
        </v-btn>
        <v-btn
          class="control-button"
          :disabled="zoom <= 0.7"
          @click="zoomOut()"
        >
          <v-icon color="primary">mdi-magnify-minus-outline</v-icon>
        </v-btn>
      </div>
      <v-btn class="control-button" @click="toggleFullsize()">
        <v-icon color="primary">mdi-fullscreen</v-icon>
      </v-btn>
    </div>
    <!-- CONTROL BUTTON END -->
    <!-- 選位結果 START -->
    <div class="seat-overview" v-if="isOverviewShow">
      <CustomCard>
        <div class="text-right pb-2">
          <a @click="closeOverview()" role="button" class="text-nine">{{ $t('seat_selector_close') }}</a>
        </div>
        <div class="overview-title">{{ $t('seat_selector_result') }}</div>
        <v-divider />
        <div>
          <v-row :class="'justify-center align-center my-0'">
            <v-col cols="6" :class="'py-0 py-sm-2'">
              <span class="text-eight">{{ $t('seat_selector_is_selected') }}</span>
              <span class="text-left text-list-title font-weight-bold">
                {{ selectedSeat.length }}
              </span>
            </v-col>
            <v-col cols="6" :class="'py-0 py-sm-2'">
              <span class="text-eight">{{ $t('seat_selector_not_selected') }}</span>
              <span
                :class="
                  `text-left text-list-title font-weight-bold ${
                    limit - selectedSeat.length === 0
                      ? 'grey--text lighten-1--text'
                      : 'red--text'
                  }`
                "
              >
                {{ limit - selectedSeat.length }}
              </span>
            </v-col>
          </v-row>
        </div>
        <v-divider />
        <div class="seat-list">
          <div
            class="seat-item"
            v-for="(seat, i) in selectedSeat"
            :key="`seats-overview-${i}`"
          >
            <div
              class="content text-title"
              role="button"
              @click="initPosition(seat)"
            >
              <b>{{ seat.row }}</b> {{ $t('seat_row') }} <b>{{ seat.column }}</b> {{ $t('seat_column') }}
            </div>
            <div class="action" role="button" @click="removeSeat(i)">
              <v-icon color="primary">mdi-delete-outline</v-icon>
            </div>
          </div>
        </div>
      </CustomCard>
    </div>
    <!-- 選位結果 END -->
    <v-snackbar v-model="snackbar" absolute bottom :timeout="5000">
      <template v-if="!isConsecuetiveAlert">
        {{ $t('seat_selector_selected_removed') }}
        <v-btn @click="rollbackItem()" outlined rounded color="white">{{ $t('seat_selector_selected_restored') }}</v-btn>
      </template>
      <template v-else>
        {{ $t('confirm_st_consecutive_seat_notification') }}
      </template>
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import CustomCard from "@/components/CustomCard.vue";
window.drag = false
window.timer = null
window.center = { x: 0, y: 0}
window.pos = { x: 0, y: 0}
window.origin = { x: 0, y: 0}
window.pos = { x: 0, y: 0}

export default {
  components: {
    CustomCard
  },
  props: {
    seats: {
      default: [],
      type: Array
    },
    info: {
      default: [],
      type: Array
    },
    limit: {
      default: 0,
      type: Number
    },
    overviewShow: {
      default: true,
      type: Boolean
    }
  },
  data() {
    return {
      isWheeling: false,
      isZooming: false,
      ticking: false,
      init: true,
      drag: false,
      snackbar: false,
      isOverviewShow: true,
      fullsizeRatio: null,
      originSize: {
        w: 0,
        h: 0
      },
      contentSize: {
        w: 0,
        h: 0
      },
      origin: {
        x: 0,
        y: 0
      },
      pos: {
        x: 0,
        y: 0
      },
      center: {
        x: 0,
        y: 0
      },
      isConsecuetiveAlert: false,
      tempSelector: null,
      originSelected: [],
      selectedSeat: [],
      zoom: 1,
      timer: null,
      removeTimeout: null,
    };
  },
  watch: {
    overviewShow: {
      deep: true,
      handler(newVal) {
        this.isOverviewShow = newVal;
      }
    },
    seats: {
      deep: true,
      handler(newVal) {
        if (this.init && newVal.length) {
          this.initContentSize();
        }
      }
    },
    info: {
      deep: true,
      handler(newVal, preVal) {
        this.selectedSeat = [...newVal];
        if(newVal.length > 1 && (newVal !== preVal || newVal.length !== preVal.length)) {
          this.isConsecuetiveAlert = false;
          let isConsecuetive = true;
          if(newVal.length === this.limit) {
            let array = newVal.sort((a,b) => a.index - b.index);
            array.forEach((s, i) => {
              if (
                i + 1 <= newVal.length - 1 && 
                !!s && !!newVal[i+1] && 
                !( newVal[i+1].index === s.index+1 && s.row === newVal[i+1].row)
              ) {
                isConsecuetive = false;
              }
            })
          }

          if(!isConsecuetive) {
            this.isConsecuetiveAlert = true;
            this.snackbar = true;
          } 
        }
        if(this.init && newVal !== preVal) {
          this.originSelected = [...newVal];
        }
      }
    },
  },
  mounted() {
    const seatContent = document.querySelector('.seats-content')
    const wrapper = document.querySelector('.seats-wrapper')
    window.addEventListener("resize", () => {
      if(this.seats.length)
        this.initContentSize()
    })
    
    wrapper.onscroll = (e) => {
      if(this.init || !!this.fullsizeRatio || this.isZooming || this.isWheeling) return
      clearTimeout(timer)
      window.timer = setTimeout(() => {
        window.center = {
          x: (wrapper.scrollLeft + wrapper.clientWidth / 2) / (seatContent.clientWidth),
          y: (wrapper.scrollTop + wrapper.clientHeight / 2) / (seatContent.clientHeight)
        }
        this.$forceUpdate()
      }, 300)
    }
    wrapper.addEventListener("wheel", e => {
      if (window.drag) return
      this.isWheeling = true
      e.preventDefault();
      // console.log(e.deltaY)
      if (e.deltaY < 0) {
        this.zoomOut(e);
      } else {
        this.zoomIn(e);
      }
      this.isWheeling = false
      return
    });

    document.body.onmousemove = e => this.dragMove(e)
    document.body.onmouseup = e => this.dragEnd(e)
  },
  methods: {
    closeOverview() {
      this.isOverviewShow = false;
      this.$emit("updateOverview", this.isOverviewShow);
    },
    removeSeat(index) {
      window.drag = false;
      this.isConsecuetiveAlert = false;
      this.tempSelector = [...this.selectedSeat];
      this.selectedSeat.splice(index, 1);
      this.snackbar = true;
      
      this.removeTimeout = setTimeout(() => {
        this.tempSelector = null;
      }, 5000);
      // console.log("removeSeat", this.selectedSeat);
      this.$emit("update", this.selectedSeat);
    },
    rollbackItem() {
      clearTimeout(this.removeTimeout);
      window.drag = false;
      this.snackbar = false;

      if(!this.tempSelector || !this.tempSelector.length) return;
      
      this.selectedSeat = [];
      this.selectedSeat = [...this.tempSelector];

      // console.log("removeSeat", this.tempSelector);
      this.tempSelector = null;
    
      this.$emit("update", this.selectedSeat);
      this.$forceUpdate();
    },
    isSelected(seat, useOrigin = undefined) {
      if(!this.selectedSeat.length && !useOrigin) return 0;
      
      let filterResult = (!useOrigin ? this.selectedSeat : this.originSelected).filter(select => {
        return select.row == seat.row && select.column == seat.column;
      });

      return filterResult.length > 0;
    },
    toggleSeat(e, seat) {
      if (window.drag || !!seat && seat.isRowName) return;
      if(this.removeTimeout) {
        this.snackbar = false;
        clearTimeout(this.removeTimeout);
      }
      if(window.timer) {
        window.drag = false;
        clearTimeout(window.timer);
      }
      let index = -1;
      this.selectedSeat.forEach((s, i) => {
        if (seat.row === s.row && seat.column === s.column) index = i;
      });

      if (index > -1) {
        this.removeSeat(index);
      } else {
        if (this.limit - this.selectedSeat.length > 0) {
          this.selectedSeat.push(seat);
        } 
      }

      this.$emit("update", this.selectedSeat);
    },
    initContentSize() {
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      this.isLoading = true
      if (this.originSize.w === 0) {
        let idx = this.seats.reduce((accr, row, idx, ary) => {
          return !!row && row.length > this.seats[accr].length ? idx : accr;
        }, 0);

        this.originSize = {
          w: this.seats[idx].length * (44)+ (this.$vuetify.breakpoint.xs ? 0 : 200),
          h: this.seats.length * (52) + (400)
        };
        
        window.contentSize = { 
          w: this.originSize.w * this.zoom,
          h: this.originSize.h * this.zoom 
         };
      } else {
        window.contentSize = {
          w: this.originSize.w * this.zoom,
          h: this.originSize.h * this.zoom,
        }
        
        seatContent.style.width = `${window.contentSize.w}px`
        seatContent.style.height = `${window.contentSize.h}px`
        
        wrapper.scrollTo({
          left: window.contentSize.w * window.center.x - wrapper.clientWidth / 2,
          top: window.contentSize.h * window.center.y - wrapper.clientHeight / 2
        })
      }

      if(this.init) {
        seatContent.style.width = `${window.contentSize.w}px`
        seatContent.style.height = `${window.contentSize.h}px`
      }
      window.pos = {
        x: wrapper.scrollLeft,
        y: wrapper.scrollTop
      }
      this.$forceUpdate()
      
      if(this.init)
        this.initPosition()
      else {
        this.getCenter()
        this.initPosition()
      }
      
      this.isLoading = false
    },
    initPosition(seat) {
      let seats = Array.from(document.querySelector('.seats-content').querySelectorAll(".seat"))
      if(!seats.length) {
        setTimeout(() => {
          this.initPosition(seat)
        }, 200)
        return
      }
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      if (!wrapper) return;
      
      let content = {
        w: seatContent.clientWidth,
        h: seatContent.clientHeight
      };
      
      let activeSeats = Array.from(document.querySelector('.seats-content').querySelectorAll(".seat.is-active"));
      
      if(activeSeats.length) {
        let activeSeat = !seat
          ? activeSeats[0]
          : activeSeats.filter(
              s => s.dataset.row === seat.row && s.dataset.column === seat.column
            )[0];

        let actSeat = {
          left: activeSeat.offsetLeft,
          top: activeSeat.offsetTop
        }
        window.center = {
          x: actSeat.left / content.w,
          y: actSeat.top / content.h
        }
      } 

      this.setPosition()
      this.init = false
    },
    setPosition() {
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      let content = {
        w: seatContent.clientWidth,
        h: seatContent.clientHeight
      };
      let wrap = {
        w: wrapper.clientWidth,
        h: wrapper.clientHeight
      };
      let left = content.w * window.center.x - (wrap.w / 2)
      let top = content.h * window.center.y - (wrap.h / 2)
      left = left < 0 ? 0 : left + wrap.w > content.w ? wrap.w - content.w : left
      top = top < 0 ? 0 : top + wrap.h > content.h ? wrap.h - content.h : top
      
      wrapper.scrollTo({ left, top })
    },
    getCenter() {
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      const content = {
        w: seatContent.clientWidth,
        h: seatContent.clientHeight
      }
      const wrap = {
        w: wrapper.clientWidth,
        h: wrapper.clientHeight
      }
      window.center = {
        x: (window.pos.x + wrap.w / 2) / content.w,
        y: (window.pos.y + wrap.h / 2) / content.h
      }
    },
    dragStart(e) {
      e.preventDefault()
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      
      if (this.init || !!this.fullsizeRatio || this.isZooming || this.isWheeling || this.selection || e.ctrlKey || e.metaKey) return false;
      window.timer = setTimeout(() => {
        window.origin = {
          x: e.clientX,
          y: e.clientY
        }
        seatContent.classList.add("is-dragging")
        window.drag = true
      }, 200);
    },
    dragMove(e) {
      e.preventDefault()
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      if (!window.drag || this.fullsizeRatio || this.isZooming || this.isWheeling || this.selection || e.ctrlKey || e.metaKey) return
      
      wrapper.scrollTo({
        left: window.pos.x + (window.origin.x - e.clientX),
        top: window.pos.y + (window.origin.y - e.clientY),
      })
      
      if (e.buttons === 0) {
        if (window.timer) clearTimeout(window.timer)
        window.drag = false
        seatContent.classList.remove("is-dragging")
        this.$forceUpdate()
        this.getCenter()
      }
    },
    dragEnd(e) {
      e.preventDefault()
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      if (!window.drag || this.fullsizeRatio || this.isZooming || this.isWheeling || this.selection || e.ctrlKey || e.metaKey) return
      if (window.timer) clearTimeout(window.timer);
      window.drag = false;
      window.pos = {
        x: window.pos.x + (window.origin.x - e.clientX),
        y: window.pos.y + (window.origin.y - e.clientY),
      }
      wrapper.scrollTo({
        left: window.pos.x,
        top: window.pos.y,
      })
      seatContent.classList.remove("is-dragging")
      this.$forceUpdate()
      this.getCenter()
    },
    toggleFullsize() {
      if(this.fullsizeRatio) return
      const seatContent = this.$refs.seatContent || document.querySelector('.seats-content')
      const wrapper = this.$refs.seatWrapper || document.querySelector('.seats-wrapper')
      let isWidthAll = this.originSize.w > this.originSize.h;
      let contentSize = isWidthAll
        ? this.originSize.w 
        : this.originSize.h;
      let containerSize = isWidthAll
        ? wrapper.clientWidth - (this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm ? -20 : 360)
        : wrapper.clientHeight;

      this.fullsizeRatio = Number((containerSize / contentSize).toFixed(3));
      this.zoom = this.fullsizeRatio < 0.5 ? 0.5 : this.fullsizeRatio > 1.5 ? 1.5 : this.fullsizeRatio
      window.center = { x: 0.5, y: 0.5 }
      wrapper.scrollTo(0, 0);

      seatContent.style.width = `${this.originSize.w * this.fullsizeRatio}px`;
      seatContent.style.height = `${this.originSize.h * this.fullsizeRatio}px`;
    },
    zoomOut(e) {
      this.isZooming = true
      this.fullsizeRatio = null
      if (this.zoom <= 0.05) return;

      this.zoom -= 0.05;
      this.$forceUpdate()
      this.initContentSize();

      setTimeout(() => {
        this.isZooming = false
      }, 150);
    },
    zoomIn(e) {
      this.isZooming = true
      this.fullsizeRatio = null
      if (this.zoom >= 1.5) return

      this.zoom += 0.05;
      this.$forceUpdate()
      this.initContentSize()

      setTimeout(() => {
        this.isZooming = false
      }, 150);
    },
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-snack__wrapper {
    bottom: 16px;
  }
}

.seat-overview {
  position: absolute;
  width: 250px;
  height: auto;
  top: 20px;
  left: 15px;

  .seat-list {
    padding-bottom: 20px;
    display: block;
    max-height: calc(100vh - 470px);
    -webkit-overflow-scrolling: touch;
    overflow-x: hidden;
    overflow-y: auto;
  }
  .seat-list .seat-item {
    display: flex;
    border-radius: 6px;
    border: solid 1px $grey-lighten-1;
    margin-top: 15px;

    > * {
      padding: 12px 16px;
    }
    .content {
      flex: 1;
      flex-grow: 1;
    }
  }
  .overview-title {
    color: white;
    font-size: 20px;
    padding: 12px;
    line-height: 1;
    background: $primary-1;
    margin-bottom: 15px;
  }

  .v-card {
    margin: 0 !important;
    padding: 15px !important;
  }
}

.seats-container {
  position: relative;

  &.is-fullsize {
    position: fixed;
    z-index: 2999;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    background: white;
  }
}

.seats-wrapper {
  position: relative;
  display: block;
  overflow: auto;
  width: 100%;
  height: calc(100vh - 264px);
  padding-top: 64px;
  padding-left: 270px;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
}

.seat-status {
  position: absolute;
  width: 100%;
  display: flex;
  top: 0;
  left: 0;
  white-space: nowrap;
  align-items: center;
  justify-content: center;
  padding: 18px 0;
  background: $grey-lighten-2;

  .seat-item {
    margin: 0 15px;
    font-weight: 600;
    * {
      vertical-align: middle;
    }
  }

  .seat {
    display: inline-block;
    width: 24px;
    height: 24px;
    margin-right: 8px;
    border-radius: 50%;
    border: 1px solid $grey-lighten-1;

    &.is-avaliable {
      background: white;
      border: 1px solid $grey-lighten-1;
    }

    &.is-selected {
      border: 1px solid transparent;
      background: #8d8d8d;
    }

    &.is-unavaliable {
      border: 1px solid transparent;
      background: $grey-lighten-1;
    }

    &.is-active {
      border: 1px solid transparent;
      background: $accent-red !important;
    }
  }
}

.seats-content {
  display: block;
  padding: 0 36px 10px 0;
  white-space: nowrap;
  cursor: grab;
  transform-origin: 0% 0%;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: -moz-none;
  -o-user-select: none;
  user-select: none;

  &.is-dragging .seat {
    pointer-events: none;
  }

  .stage {
    position: relative;
    width: 240px;
    height: 50px;
    line-height: 50px;
    margin: 0 auto 40px;
    display: block;
    color: $primary-1;

    .after,
    .before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      display: block;
      width: 191px;
      height: 28px;
    }
    .before {
      right: 100%;
    }
    .after {
      left: 100%;
    }
  }
}

.seat-area {
  display: inline-block;
  transform-origin: 0 0;
  padding: 60px 30px 0;

  > .seat-row {
    display: inline-flex;
    height: 50px;
  }
  .seat-row {
    margin-bottom: 20px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    align-content: center;
  }

  .seats {
    display: inline-flex;
  }
  .seats .seat-row {
    height: 32px;
    align-items: flex-start;
    align-content: flex-start;
  }
}

.seats {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  align-items: flex-start;
  align-content: flex-start;
  margin-top: 40px;

  .seat {
    position: relative;
    display: inline-block;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    border-radius: 50%;
    margin: 0 6px;
    cursor: pointer;
    vertical-align: top;
    border: 1px solid $grey-lighten-1;
    box-sizing: border-box;
    background: white;

    > .v-icon {
      margin-top: -2px;
    }

    &.is-avaliable {
      border: 1px solid $grey-lighten-1;
    }

    &.is-unavaliable {
      pointer-events: none;
      color: $grey-darken-1;
      background: $grey-lighten-1;
      border-color: $grey-lighten-1;
      cursor: default;
    }

    &.is-active {
      color: white;
      background: $accent-red !important;
      border-color: $accent-red  !important;
    }

    &.is-selected {
      cursor: default;
      pointer-events: none;
      color: white;
      border: 1px solid transparent;
      background: #8d8d8d;
    }

    &.is-empty {
      background: none;
      > span {
        display: flex;
        white-space: nowrap;
      }
    }
    &.is-row-name {
      background: none;
      border: 0;
    }
    &.is-not-seat,
    &.is-empty,
    &.is-hidden {
      visibility: hidden;
      border: 0;
    }
  }
}

.control-bar {
  position: absolute;
  top: 20px;
  right: 30px;

  > .v-btn.control-button {
    display: inline-block;
    background: white;
    height: 60px;
    padding: 15px 17px;
    border-radius: 8px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
    i {
      font-size: 30px;
    }
  }

  .theme--light.v-btn.control-button.v-btn--disabled {
    background: white !important;

    .v-btn__content > i {
      color: $grey-lighten-1;
    }
  }

  .control-buttons {
    display: inline-block;
    border-radius: 8px;
    margin-right: 20px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);

    .v-btn.control-button {
      display: inline-block;
      height: 60px;
      background: white;
      padding: 15px 17px;
      box-shadow: none;

      &:first-child {
        border-radius: 8px 0 0 8px;
      }
      &:last-child {
        border-radius: 0 8px 8px 0;
      }
      i {
        font-size: 30px;
      }
    }

    .control-button + .control-button {
      border-left: 1px solid $grey-lighten-1;
    }
  }
}

@media screen and (max-width: 960px) {
  .seats-container {
    height: calc(100% - 20px);
    .order-content {
      padding-bottom: 0;
    }
  }

  .seats-wrapper {
    padding-top: 56px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    max-height: calc(100vh - 239px);

    .seats-content {
      position: relative;
      padding-top: 20px;
      .seat-area {
        display: inline-block;
        > .seat-row {
          height: 27px;
        }
      }
      .seats {
        margin-top: 20px;
        align-items: flex-start;
        align-content: flex-start;

        .seat-row {
          height: 27px;
          margin-bottom: 15px;
        }
        .seat {
          width: 27px;
          height: 27px;
          line-height: 27px;
        }
      }
      .stage {
        height: 40px;
        line-height: 40px;
        margin: 0 auto 20px;
      }
    }
  }

  .control-bar {
    top: 40px;
    right: 15px;
    .control-buttons {
      margin-right: 10px;
    }
    .control-buttons .v-btn.control-button,
    .v-btn.control-button {
      width: 36px;
      min-width: 36px;
      height: 36px;
      min-height: 36px;
      padding: 6px;
      .v-icon {
        font-size: 24px;
      }
    }
  }

  .seat-status {
    padding: 12px 0;
    .seat-item {
      font-size: 0.7rem;
      margin: 0 7px 8px;
      .seat {
        width: 12px;
        height: 12px;
      }
    }
  }

  .seat-overview {
    top: 48px;
  }
}

@media screen and (max-width: 600px) {
  .seats-wrapper {
    height: calc(100vh - 195px);
    max-height: calc(100vh - 195px);
    padding: 140px 0 250px;
  }
  .seat-overview {
    width: 100%;
    top: auto;
    left: 0;
    bottom: 0;
    border-radius: 20px 20px 0 0;

    .overview-title {
      font-size: 1rem;
      padding: 10px;
      margin-bottom: 8px;
    }

    .seat-list {
      padding-bottom: 0px;
      display: flex;
      flex-wrap: wrap;
      margin: 8px -7px 0;
      max-height: calc(100vh - 390px);
    }

    .seat-list .seat-item {
      margin: 0 7px 8px;
      width: calc(50% - 14px);

      .content {
        font-size: 1rem;
      }
      > * {
        padding: 8px 12px;
      }
    }
  }
}
</style>
